const GROUP_SIZE = 4
const GROUP_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'

export function groupLetter(number) {
  if (number >= 1 && number <= GROUP_LETTERS.length) return GROUP_LETTERS[number - 1]
  return String(number)
}

export function resolveGroupSize(tournamentOrSize) {
  const raw = typeof tournamentOrSize === 'number'
    ? tournamentOrSize
    : (tournamentOrSize?.groupSize ?? GROUP_SIZE)
  return Math.min(16, Math.max(2, raw || GROUP_SIZE))
}

export function resolveTeamsAdvancing(tournament) {
  const size = resolveGroupSize(tournament)
  const advancing = tournament?.teamsAdvancingPerGroup ?? 2
  return Math.min(size, Math.max(1, advancing))
}

export function chunkTeamsIntoGroups(teams, groupSize = GROUP_SIZE) {
  const size = resolveGroupSize(groupSize)
  const sorted = [...(teams || [])].sort((a, b) => a.seed - b.seed)
  const groups = []
  for (let i = 0; i < sorted.length; i += size) {
    groups.push(sorted.slice(i, i + size))
  }
  return groups
}

export function flattenGroupOrder(groups) {
  return groups.flat().map((t) => t.id)
}

function standingsFrom(matches, teams) {
  const byId = new Map()

  const ensure = (teamId, name) => {
    if (!teamId) return
    if (!byId.has(teamId)) {
      const team = (teams || []).find((t) => t.id === teamId)
      byId.set(teamId, {
        teamId,
        teamName: name || team?.name || 'Lag',
        subtitle: team?.subtitle || null,
        played: 0,
        won: 0,
        drawn: 0,
        lost: 0,
        goalsFor: 0,
        goalsAgainst: 0,
        points: 0
      })
    }
  }

  for (const match of matches || []) {
    ensure(match.homeTeamId, match.homeTeamName)
    ensure(match.awayTeamId, match.awayTeamName)
    if (!match.homeTeamId || !match.awayTeamId) continue
    if (match.status !== 'COMPLETED' || match.homeScore == null || match.awayScore == null) continue

    const home = byId.get(match.homeTeamId)
    const away = byId.get(match.awayTeamId)
    home.played += 1
    away.played += 1
    home.goalsFor += match.homeScore
    home.goalsAgainst += match.awayScore
    away.goalsFor += match.awayScore
    away.goalsAgainst += match.homeScore

    if (match.homeScore > match.awayScore) {
      home.won += 1
      home.points += 3
      away.lost += 1
    } else if (match.homeScore < match.awayScore) {
      away.won += 1
      away.points += 3
      home.lost += 1
    } else {
      home.drawn += 1
      away.drawn += 1
      home.points += 1
      away.points += 1
    }
  }

  for (const team of teams || []) {
    ensure(team.id, team.name)
  }

  return [...byId.values()].sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points
    const gdA = a.goalsFor - a.goalsAgainst
    const gdB = b.goalsFor - b.goalsAgainst
    if (gdB !== gdA) return gdB - gdA
    if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor
    return a.teamName.localeCompare(b.teamName, 'nb')
  })
}

export function tablesFor(tournament) {
  const matches = tournament.matches || []
  const teams = tournament.teams || []
  if (!matches.length && !teams.length) return []

  switch (tournament.format) {
    case 'KNOCKOUT':
      return []
    case 'ROUND_ROBIN': {
      const rows = standingsFrom(matches, teams)
      return rows.length ? [{ groupNumber: 1, title: 'Tabell', rows }] : []
    }
    case 'GROUPS_THEN_KNOCKOUT': {
      const groups = chunkTeamsIntoGroups(teams, resolveGroupSize(tournament))
      return groups.map((groupTeams, idx) => {
        const groupNum = idx + 1
        const groupMatches = matches.filter((m) => m.roundNumber === groupNum)
        return {
          groupNumber: groupNum,
          title: `Gruppe ${groupLetter(groupNum)}`,
          rows: standingsFrom(groupMatches, groupTeams)
        }
      })
    }
    default:
      return []
  }
}

export function groupCount(teams, groupSize = GROUP_SIZE) {
  if (!teams?.length) return 0
  return Math.ceil(teams.length / resolveGroupSize(groupSize))
}

export function groupCountForTournament(tournament) {
  return groupCount(tournament.teams, resolveGroupSize(tournament))
}

export function knockoutStartRound(tournament) {
  if (tournament.format !== 'GROUPS_THEN_KNOCKOUT') return 1
  return groupCountForTournament(tournament) + 1
}

export function isKnockoutMatch(tournament, match) {
  if (tournament.format === 'KNOCKOUT') return true
  if (tournament.format === 'GROUPS_THEN_KNOCKOUT') {
    return match.roundNumber >= knockoutStartRound(tournament)
  }
  return false
}

export function knockoutMatches(tournament) {
  return (tournament.matches || [])
    .filter((m) => isKnockoutMatch(tournament, m))
    .sort((a, b) => a.roundNumber - b.roundNumber || a.matchNumber - b.matchNumber)
}

export function firstKnockoutRound(tournament) {
  const ko = knockoutMatches(tournament)
  if (!ko.length) return []
  const minRound = Math.min(...ko.map((m) => m.roundNumber))
  return ko.filter((m) => m.roundNumber === minRound)
}

export function roundLabel(tournament, roundNumber) {
  if (tournament.format === 'GROUPS_THEN_KNOCKOUT') {
    const start = knockoutStartRound(tournament)
    if (roundNumber < start) return `Gruppe ${groupLetter(roundNumber)}`
    const koRound = roundNumber - start + 1
    const koMatches = knockoutMatches(tournament).filter((m) => m.roundNumber === roundNumber)
    if (koMatches.length === 1) return 'Finale'
    if (koMatches.length === 2) return 'Semifinale'
    if (koMatches.length === 4) return 'Kvartfinale'
    return `Cup runde ${koRound}`
  }
  if (tournament.format === 'KNOCKOUT') {
    const inRound = (tournament.matches || []).filter((m) => m.roundNumber === roundNumber)
    if (inRound.length === 1) return 'Finale'
    if (inRound.length === 2) return 'Semifinale'
    if (inRound.length === 4) return 'Kvartfinale'
    return `Runde ${roundNumber}`
  }
  return `Runde ${roundNumber}`
}

export function groupStageComplete(tournament) {
  if (tournament.format !== 'GROUPS_THEN_KNOCKOUT') return false
  const gc = groupCountForTournament(tournament)
  const groupMatches = (tournament.matches || []).filter((m) => m.roundNumber >= 1 && m.roundNumber <= gc)
  if (!groupMatches.length) return false
  return groupMatches.every((m) => m.status === 'COMPLETED')
}

export function hasKnockoutBracket(tournament) {
  if (tournament.format === 'KNOCKOUT') return (tournament.matches || []).length > 0
  if (tournament.format !== 'GROUPS_THEN_KNOCKOUT') return false
  return (tournament.matches || []).some((m) => m.roundNumber >= knockoutStartRound(tournament))
}

export { GROUP_SIZE }
